import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { useRefreshBookings } from '../queries.ts';
import { fmtRange } from '../time.ts';
import { isStaff, type Booking, type Me, type Room } from '../types.ts';
import { BookingDialog } from './BookingDialog.tsx';
import { ErrorText } from './ErrorText.tsx';
import { Modal } from './Modal.tsx';
import { useToast } from './Toast.tsx';

export function BookingDetails({
  me,
  rooms,
  bookingId,
  extra,
  onClose,
}: {
  me: Me;
  rooms: Room[];
  bookingId: string;
  extra?: { person?: string | null; company?: string | null };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshBookings();
  const [editing, setEditing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [scope, setScope] = useState<'one' | 'following'>('one');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const q = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: () => api.get<Booking>(`/api/bookings/${bookingId}`),
  });
  const b = q.data;
  if (!b) return null;
  if (editing)
    return <BookingDialog me={me} rooms={rooms} initial={{}} editing={b} onClose={onClose} />;

  const room = rooms.find((r) => r.id === b.roomId);
  const now = new Date();
  const started = new Date(b.start) <= now;
  const ongoing = started && new Date(b.end) > now;
  const active = b.status === 'active';
  const staff = isStaff(me);
  const canEdit = active && (staff || !started);
  const canCancel = active && (staff || !started);

  const run = async (fn: () => Promise<unknown>, message: string) => {
    setError(null);
    try {
      await fn();
      toast(message);
      refresh();
      onClose();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <Modal title={b.title || t('booking.titlePlaceholder')} onClose={onClose}>
      <p>
        <b>{room?.name}</b> · {fmtRange(b.start, b.end)}
      </p>
      <p className="k">
        {b.attendees} {t('common.people')}
        {b.seriesId ? ` · ${t('booking.series')}` : ''}
        {extra?.person ? ` · ${extra.person}` : ''}
        {extra?.company ? ` · ${extra.company}` : ''}
        {ongoing ? ` · ${t('booking.started')}` : ''}
        {!active ? ` · ${t('booking.statusCancelled')}` : ''}
      </p>
      {b.cancelReason && <p className="k">{b.cancelReason}</p>}
      {confirmCancel ? (
        <div className="card">
          <p>{t('booking.cancelConfirm')}</p>
          {b.seriesId && (
            <fieldset>
              <legend>{t('booking.scopeQuestion')}</legend>
              <label className="check">
                <input
                  type="radio"
                  name="cscope"
                  checked={scope === 'one'}
                  onChange={() => setScope('one')}
                />
                {t('booking.cancelOne')}
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="cscope"
                  checked={scope === 'following'}
                  onChange={() => setScope('following')}
                />
                {t('booking.cancelFollowing')}
              </label>
            </fieldset>
          )}
          {staff && b.userId !== me.id && (
            <label>
              {t('booking.reason')}
              <input maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
          )}
          <div className="row">
            <button className="btn alt" onClick={() => setConfirmCancel(false)}>
              {t('common.no')}
            </button>
            <button
              className="btn del"
              onClick={() =>
                run(
                  () =>
                    api.post(`/api/bookings/${b.id}/cancel`, {
                      scope,
                      ...(reason ? { reason } : {}),
                    }),
                  t('booking.cancelled'),
                )
              }
            >
              {t('booking.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="actions">
          {canEdit && (
            <button className="btn alt" onClick={() => setEditing(true)}>
              {t('common.edit')}
            </button>
          )}
          {active && ongoing && (
            <button
              className="btn alt"
              onClick={() =>
                run(() => api.post(`/api/bookings/${b.id}/end`), t('booking.endedEarly'))
              }
            >
              {t('booking.endEarly')}
            </button>
          )}
          {canCancel && (
            <button className="btn del" onClick={() => setConfirmCancel(true)}>
              {t('booking.cancel')}
            </button>
          )}
          <span className="sp" />
          <button className="btn alt" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      )}
      <ErrorText error={error} />
    </Modal>
  );
}
